/**
 * Moving platforms — kinematic one-way rides.
 *
 * MovingPlatformEntity travels along a waypoint path (its spawn cell
 * plus 1+ offsets) at a constant speed, ping-ponging or looping, and
 * optionally pausing at each endpoint. Physics-wise it behaves like a
 * travelling ONE-WAY tile row: a falling body lands on its top edge,
 * but it never blocks sideways motion or upward jumps.
 *
 * Rider-carry uses the industry-standard "move riders first" order
 * (see PlatformerScene._stepSimulation):
 *
 *   1. every platform advances and records its per-frame delta,
 *   2. carryRiders() shifts every body that was standing on a
 *      platform by that platform's delta,
 *   3. the bodies then run their own integration (input, gravity,
 *      collision) from the carried position — so a player standing
 *      still on a moving platform rides it perfectly, and walking /
 *      jumping is relative to the platform's motion.
 *
 * Landing itself happens inside PlatformerPhysics (the Y pass and
 * isGrounded read `board.movingPlatforms`), so coyote time, jump
 * buffering and friction all behave exactly as on solid ground.
 */

import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { PLATFORMER_ENTITY_DEFS } from './platformerConfig.js';
import { spawnSprite } from './entityHelpers.js';

/** Tolerance (px) for "feet are on the platform top" checks. */
export const PLATFORM_RIDE_TOLERANCE = 3;

export class MovingPlatformEntity {
    constructor(c, r, board, theme, props = {}) {
        this._theme = theme;
        const cs = board.cellSize;
        this.cell = { c, r };
        this.wCells = Math.max(1, Math.round(props.w || 3));
        this.w = this.wCells * cs;
        // A thin slab — half a cell tall reads as "platform", and the
        // top edge sits flush with the spawn cell's top so it lines up
        // with the tile grid the designer placed it on.
        this.h = Math.round(cs * 0.5);
        this.x = c * cs;
        this.y = r * cs;
        this.alive = true;
        this.isMovingPlatform = true;

        // ── Path ────────────────────────────────────────────────
        // Waypoints are CELL OFFSETS from the spawn cell, starting at
        // the implicit {0,0}. Authored via dx/dy (a 2-point path) in
        // the editor; hand-written stage JSON may supply a full
        // `waypoints: [[dc,dr], ...]` list for multi-leg routes.
        const pts = [{ x: this.x, y: this.y }];
        if (Array.isArray(props.waypoints) && props.waypoints.length) {
            for (const wp of props.waypoints) {
                const dc = Array.isArray(wp) ? wp[0] : wp?.dc;
                const dr = Array.isArray(wp) ? wp[1] : wp?.dr;
                if (!Number.isFinite(dc) || !Number.isFinite(dr)) continue;
                pts.push({ x: (c + dc) * cs, y: (r + dr) * cs });
            }
        } else {
            const dc = Number.isFinite(props.dx) ? props.dx : 4;
            const dr = Number.isFinite(props.dy) ? props.dy : 0;
            pts.push({ x: (c + dc) * cs, y: (r + dr) * cs });
        }
        if (pts.length < 2) pts.push({ x: this.x, y: this.y });
        this._points = pts;

        this.speed = Number.isFinite(props.speed) ? Math.max(0, props.speed) : 1.2;
        this.mode = props.mode === 'loop' ? 'loop' : 'pingpong';
        this._pauseFrames = Math.max(0, Math.round(
            (Number.isFinite(props.pauseSec) ? props.pauseSec : 0.5) * 60,
        ));

        // Travel state: segment index + progress (px along segment),
        // direction +1 / -1 (pingpong reverses at the ends).
        this._seg = 0;
        this._segPos = 0;
        this._dir = 1;
        this._waitLeft = 0;

        /** Per-frame world delta — what riders inherit this frame. */
        this.frameDx = 0;
        this.frameDy = 0;
        /** Bounds BEFORE this frame's move — rider detection compares
         *  feet against where the platform top WAS when they stood on it. */
        this.prevX = this.x;
        this.prevY = this.y;

        this.display = new Container();
        this._buildVisual();
        this.display.x = this.x;
        this.display.y = this.y;
    }

    _buildVisual() {
        const def = PLATFORMER_ENTITY_DEFS['platform.moving'];
        const sprite = spawnSprite(this._theme, def.slot, this.w, this.h);
        if (sprite) {
            this.display.addChild(sprite);
            return;
        }
        // Procedural slab: stone body, lit top lip, rivet dots.
        const g = new Graphics();
        g.roundRect(0, 0, this.w, this.h, 6);
        g.fill(0x57534e);
        g.roundRect(0, 0, this.w, Math.max(4, this.h * 0.28), 6);
        g.fill(0x8b5cf6);
        g.roundRect(0, 0, this.w, this.h, 6);
        g.stroke({ color: 0x292524, width: 2, alpha: 0.8 });
        const n = this.wCells;
        for (let i = 0; i < n; i++) {
            g.circle((i + 0.5) * (this.w / n), this.h * 0.62, 2.5);
            g.fill({ color: 0xd6d3d1, alpha: 0.7 });
        }
        this.display.addChild(g);
    }

    /** Segment length in px (cached lazily — paths are tiny). */
    _segLen(i) {
        const a = this._points[i];
        const b = this._points[i + 1];
        return Math.hypot(b.x - a.x, b.y - a.y);
    }

    update() {
        if (!this.alive) return;
        this.prevX = this.x;
        this.prevY = this.y;
        this.frameDx = 0;
        this.frameDy = 0;

        if (this._waitLeft > 0) { this._waitLeft -= 1; return; }
        if (this.speed <= 0 || this._points.length < 2) return;

        // Advance along the current segment; spill leftover distance
        // into the next segment so corners don't slow the platform.
        let remaining = this.speed;
        let guard = 8;
        while (remaining > 0 && guard-- > 0) {
            const segCount = this._points.length - 1;
            const len = this._segLen(this._seg);
            const target = this._dir > 0 ? len : 0;
            const toGo = this._dir > 0 ? (len - this._segPos) : this._segPos;
            if (toGo > remaining) {
                this._segPos += this._dir * remaining;
                remaining = 0;
            } else {
                remaining -= toGo;
                this._segPos = target;
                // Reached the end of this segment — move to the next
                // one, reverse (pingpong), or wrap (loop).
                if (this._dir > 0) {
                    if (this._seg + 1 < segCount) {
                        this._seg += 1; this._segPos = 0;
                    } else if (this.mode === 'loop') {
                        this._seg = 0; this._segPos = 0;
                        this._waitLeft = this._pauseFrames;
                        // Loop teleports back through the path start —
                        // for a closed feel authors add a return leg.
                        remaining = 0;
                    } else {
                        this._dir = -1;
                        this._waitLeft = this._pauseFrames;
                        remaining = 0;
                    }
                } else {
                    if (this._seg > 0) {
                        this._seg -= 1; this._segPos = this._segLen(this._seg);
                    } else {
                        this._dir = 1;
                        this._waitLeft = this._pauseFrames;
                        remaining = 0;
                    }
                }
            }
        }

        const a = this._points[this._seg];
        const b = this._points[this._seg + 1];
        const len = this._segLen(this._seg) || 1;
        const t = Math.max(0, Math.min(1, this._segPos / len));
        const nx = a.x + (b.x - a.x) * t;
        const ny = a.y + (b.y - a.y) * t;
        this.frameDx = nx - this.x;
        this.frameDy = ny - this.y;
        this.x = nx;
        this.y = ny;
        this.display.x = this.x;
        this.display.y = this.y;
    }

    /** Never a collision verdict — landing is handled by the physics. */
    onCollidePlayer() { return null; }

    destroy() {
        this.display.removeChildren();
        if (this.display.parent) this.display.parent.removeChild(this.display);
    }
}

/**
 * True when `body` is standing on `p` — feet within tolerance of the
 * platform's PRE-MOVE top edge and horizontally overlapping its
 * pre-move span. Called after platforms moved but before bodies
 * integrate, hence the prev* coordinates.
 */
export function isRidingPlatform(body, p) {
    if (!p.alive) return false;
    if ((body.vy || 0) < 0) return false;                 // jumping up & away
    const feet = body.y + body.h;
    if (Math.abs(feet - p.prevY) > PLATFORM_RIDE_TOLERANCE + Math.abs(p.frameDy)) return false;
    return body.x + body.w > p.prevX && body.x < p.prevX + p.w;
}

/**
 * "Move riders first": shift every body standing on a platform by that
 * platform's per-frame delta. Riders get glued to the top edge so a
 * platform moving up never leaves them a frame behind (gravity would
 * otherwise make them flicker between grounded/airborne).
 */
export function carryRiders(platforms, bodies) {
    for (const p of platforms) {
        if (!p.alive || (p.frameDx === 0 && p.frameDy === 0)) continue;
        for (const body of bodies) {
            if (!body || body.alive === false) continue;
            if (!isRidingPlatform(body, p)) continue;
            body.x += p.frameDx;
            body.y = p.y - body.h;
            body.onGround = true;
        }
    }
}
